import UrlHelper from './UrlHelper';
import UIHelper from './UIHelper';
import ASSET_TYPES from '../types/AssetTypes';
import { DEFAULT_OFFSET, DEFAULT_ITEMS_PER_PAGE } from '../../../Pagination/Pagination';

/**
 * @typedef {import('../types/ContentItems').ContentItemsResponse} ContentItemsResponse
 * @typedef {import('../types/ContentItems').PlaylistContentItem} PlaylistContentItem
 * @typedef {import('../types/Assignments').Assignment} Assignment
 */

export default class LoadHelper {
  /**
   * Sets the include sub-folders parameter in the query string.
   * @param {string} query - The original query string.
   * @param {string} [defaultParent] - defaultParent if none-set
   * @returns {string} The modified query string with include sub-folders parameter.
   */
  static setIncludeSubFoldersParam = (query, defaultParent = '/') => {
    const parent = UrlHelper.getUrlParam(query, 'parent', defaultParent);

    return UrlHelper.setUrlParam(query, 'parent', `${parent}*`);
  };

  /**
   * Sets the count parameter in the query string.
   * @param {string} query - The original query string.
   * @param {number} count - The count value to set.
   * @returns {string} The modified query string with count parameter.
   */
  static setCountParam = (query, count) => {
    return UrlHelper.setUrlParam(query, 'count', count);
  };

  /**
   * Builds a assets query for loadAssets()
   * @param {string} search - The URL search query.
   * @returns {string} The normalized query string.
   */
  static buildAssetsQuery = search => {
    let query = search?.length ? search : '';
    const validTypes = Object.values(ASSET_TYPES);
    const types = UrlHelper.getUrlParam(query, 'type', validTypes).filter(t =>
      validTypes.includes(t),
    );
    const offset = UrlHelper.getUrlParam(query, 'offset', DEFAULT_OFFSET);
    const count = UrlHelper.getUrlParam(query, 'count', DEFAULT_ITEMS_PER_PAGE);

    query = UrlHelper.setUrlParam(query, 'type', types);
    query = LoadHelper.setIncludeSubFoldersParam(query);
    query = UrlHelper.setUrlParam(query, 'offset', offset);
    query = UrlHelper.setUrlParam(query, 'count', count);
    query = UrlHelper.setUrlParam(query, 'approved', 'true');
    query = UrlHelper.setUrlParam(query, 'sort', 'modifiedDate+desc+name+asc');
    query = UrlHelper.setUrlParam(query, 'isTemplate', 0);

    return query;
  };

  /**
   *
   * Mutates and Maps content items to assets structure.
   * @param {ContentItemsResponse} contentItems - The content items response.
   * @returns {ContentItemsResponse} The mapped assets structure.
   */
  static CleanUpFacetFields = contentItems => {
    // example data of type + counts:
    //  [  "sign",  16,  "campaign",  2,  "video",  2,  "folder",  1,  "playlist",  1,  "sequence",  1]
    // should remove:
    // "folder",  1,  "sequence",  1
    contentItems.facet_fields.type = contentItems.facet_fields.type.reduce(
      (acc, value, index, list) => {
        if (index % 2 !== 0) {
          return acc;
        }

        if (!Object.values(ASSET_TYPES).includes(value)) {
          return acc;
        }

        acc.push(value, list[index + 1]);

        return acc;
      },
      [],
    );

    return contentItems;
  };

  /**
   * Parses playlist data from a playlist asset.
   * Takes the last file’s width/height and sums durations
   * This is intended, it silently ignores inconsistent dimensions
   * @param {PlaylistContentItem} playlistAsset - The playlist asset object.
   * @returns {{width: number, height: number, duration: number}} The parsed playlist data.
   */
  static parsePlaylistData = playlistAsset => {
    let width = 0;
    let height = 0;
    let duration = 0;

    for (const file of playlistAsset.files) {
      width = file.width;
      height = file.height;
      duration += file.duration || 0;
    }

    return {
      width,
      height,
      duration,
    };
  };

  /**
   * Builds a assets query for loadAssignments()
   * @param {string} search - The URL search query.
   * @param {number} campaignId - The campaign ID to filter assignments.
   * @param {string} rootParent Root Parent  that the query can navigate to
   * @returns {string} The normalized query string.
   */
  static buildAssignmentsQuery = (search, campaignId, rootParent = '/') => {
    let query = search?.length ? search : '';
    const parent = UrlHelper.getUrlParam(query, 'parent', rootParent);
    const fallbackParent = `${rootParent}${parent.endsWith('*') ? '*' : ''}`;

    // if parent value is not withing rootparent, forcing it to be that.
    query = UrlHelper.setUrlParam(
      query,
      'parent',
      UIHelper.location.pathIsWithin(parent, rootParent) ? parent : fallbackParent,
    );
    query = UrlHelper.setUrlParam(query, 'campaign_id', campaignId);
    query = UrlHelper.setUrlParam(query, 'count', 1000000);

    return query;
  };

  /**
   * Normalizes assignment data by mapping device_id to id and converting it to a number.
   * @param {Assignment[]} assignments - The array of assignment objects to normalize.
   * @returns {Assignment[]} The normalized array of assignment objects with id field.
   */
  static normalizeAssignments = assignments => {
    return assignments.map(assignment => ({
      ...assignment,
      id: assignment.device_id || assignment.location_id,
    }));
  };
}
